import { createContext } from 'react';

export const THEME_STORAGE_KEY = 'portfolio-theme';

export const THEMES = ['light', 'dark'] as const;

export type Theme = (typeof THEMES)[number];

export interface ThemeContextValue {
  theme: Theme;
  /** True until the stored/system preference has been read on the client. */
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
}

export const ThemeContext = createContext<ThemeContextValue | null>(null);

export function isTheme(value: unknown): value is Theme {
  return typeof value === 'string' && (THEMES as readonly string[]).includes(value);
}

/** Reads the persisted preference, falling back to the OS setting. */
export function resolveInitialTheme(): Theme {
  if (typeof window === 'undefined') {
    return 'light';
  }

  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    if (isTheme(stored)) {
      return stored;
    }
  } catch {
    // Storage can be unavailable (private mode, blocked cookies). Fall through.
  }

  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export function applyTheme(theme: Theme): void {
  if (typeof document === 'undefined') {
    return;
  }
  document.documentElement.dataset.theme = theme;
  document.documentElement.style.colorScheme = theme;
}
